import React from 'react';
import { styled, Radio as MuiRadio } from '@mui/material';
import clsx from 'clsx';
import { omit } from 'lodash-es';
import { radioClasses, getRadioIconStyle, getSelectedRadioIcon, getStyled, } from './style';
import { extractAriaProps } from '../../Shared/props';
import RadioGroupContext from './context';
import { useUniqueId } from '../../hooks/useUniqueId';
const RadioIcon = styled('span')((props) => getRadioIconStyle(props.theme));
const SelectedRadioIcon = styled('span')((props) => getSelectedRadioIcon(props.theme));
const RadioBase = React.forwardRef((props, ref) => {
    const groupContext = React.useContext(RadioGroupContext);
    const { children, value, className, endIcon, icon, checkedIcon, id, onChange, inputProps, description, sx, ...restProps } = props;
    const getId = useUniqueId('radio');
    const ariaDescId = getId('desc');
    const [ariaProps, rest] = extractAriaProps(restProps);
    let { disabled: radioDisabled, checked: radioChecked, name: radioName, onChange: onRadioChange, } = props;
    if (groupContext) {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        radioDisabled = radioDisabled || groupContext.disabled;
        const onInnerRadioChange = (e, checked) => {
            if (!radioDisabled) {
                onChange?.(e, checked);
                groupContext?.onChange(value);
            }
        };
        radioChecked = groupContext.value === value;
        radioName = groupContext.name;
        onRadioChange = onInnerRadioChange;
    }
    return (React.createElement("div", { className: clsx(radioClasses.root, className, {
            [radioClasses.disabled]: radioDisabled,
        }) },
        React.createElement("label", { className: radioClasses.title, htmlFor: id },
            React.createElement(MuiRadio, { disableRipple: true, sx: sx, icon: icon ?? (React.createElement(RadioIcon, { className: clsx(radioClasses.icon, {
                        [radioClasses.disabled]: radioDisabled,
                    }), "aria-hidden": true })), ref: ref, checkedIcon: checkedIcon ?? (React.createElement(SelectedRadioIcon, { className: clsx(radioClasses.checkedIcon, {
                        [radioClasses.disabled]: radioDisabled,
                    }), "aria-hidden": true })), checked: radioChecked, name: radioName, value: value, disabled: radioDisabled, className: radioClasses.base, 
                // Tips:
                // When the Radio is focused, all AX announces depend on the Input element in the MUI Radio.
                // Therefore, disabled, aria-* and other AX settings are all based on this Input element.
                inputProps: {
                    'aria-label': typeof children === 'string' ? children : undefined,
                    'aria-describedby': description ? ariaDescId : undefined,
                    'aria-disabled': radioDisabled,
                    // According to AX requirements, the disabled state still needs to support focus.
                    // By default, the input will not be disabled, but only disabled at the visual level.
                    disabled: false,
                    ...ariaProps,
                    ...inputProps,
                }, onChange: onRadioChange, ...rest }),
            Boolean(children) && (React.createElement("span", { "aria-hidden": true, className: radioClasses.label }, children)),
            Boolean(endIcon) && (React.createElement("span", { "aria-hidden": true, className: radioClasses.endIcon }, endIcon))),
        Boolean(description) && (React.createElement("span", { id: ariaDescId, className: radioClasses.description }, description))));
});
const StyledRadio = styled(RadioBase)(({ theme }) => getStyled(theme));
const Radio = React.forwardRef((props, ref) => {
    const innerProps = omit(props, ['style']);
    return React.createElement(StyledRadio, { ...innerProps, ref: ref });
});
export default Radio;
